import { useCallback, useContext } from 'react';
import type { Fund, Portfolio, PortfolioNodeDesignationEnum } from 'venn-api';
import { getDefaultPortfolioParseResultCategory, remapInvestment, serializePath } from '../review/helpers';
import { MultiPortfolioReviewContext } from '../components/common/MultiPortfolioReviewContext';
import type { GetRowIdParams } from 'ag-grid-community';
import type { RowData } from '../../types';
import { assertExhaustive, useHasFF } from 'venn-utils';

export const getRowId = (row: GetRowIdParams<RowData<Portfolio>>): string => row.data.path.join('|');

export const useSharedMultiPortfolioRightPaneContent = (portfolio: Portfolio) => {
  const {
    data: { selectedIndex, parsedResults },
    actions: { updatePortfolio, includeInvestment, excludeInvestment, onChangeDesignation, assignInvestment },
  } = useContext(MultiPortfolioReviewContext);

  const hasManualStrategyAssignment = useHasFF('rcm_strategy_enforcement_ff');

  const accessMode = (() => {
    const selectedPortfolio = parsedResults[selectedIndex]!;
    const defaultParseResultCategory = getDefaultPortfolioParseResultCategory(selectedPortfolio);
    switch (defaultParseResultCategory) {
      case 'existing':
        return 'OVERWRITE_EXISTING' as const;
      case 'new':
        return 'CREATE_NEW' as const;
      default:
        return assertExhaustive(defaultParseResultCategory);
    }
  })();

  const applyInvestmentRemap = useCallback(
    (path: number[], fund: Fund) => {
      const isExcluded = parsedResults?.[selectedIndex]?.excludedInvestments?.has(serializePath(path).value);
      if (isExcluded) return;

      const newPortfolio = remapInvestment(portfolio, path, fund);
      updatePortfolio(selectedIndex, newPortfolio);

      if (hasManualStrategyAssignment) {
        assignInvestment(selectedIndex, fund, undefined, false); // Automatic assignment via mapping
      }
    },
    [updatePortfolio, selectedIndex, parsedResults, portfolio, assignInvestment, hasManualStrategyAssignment],
  );

  const includeInvestmentInSelectedPortfolio = useCallback(
    (path: number[]) => includeInvestment(selectedIndex, path),
    [includeInvestment, selectedIndex],
  );

  const excludeInvestmentFromSelectedPortfolio = useCallback(
    (path: number[]) => excludeInvestment(selectedIndex, path),
    [excludeInvestment, selectedIndex],
  );

  const changeInvestmentDesignationInSelectedPortfolio = useCallback(
    (path: number[], designation: PortfolioNodeDesignationEnum) =>
      onChangeDesignation(selectedIndex, path, designation),
    [onChangeDesignation, selectedIndex],
  );

  return {
    accessMode,
    applyInvestmentRemap,
    includeInvestment: includeInvestmentInSelectedPortfolio,
    excludeInvestment: excludeInvestmentFromSelectedPortfolio,
    changeInvestmentDesignation: changeInvestmentDesignationInSelectedPortfolio,
  };
};
